<template>
	<div>
		<!-- dialog -->
		<v-dialog v-model="dialog" max-width="300">
			<v-form ref="form" @submit.prevent="submit" :disabled="submitLoading">
				<v-card>
					<v-card-title>
						<span class="me-n6">
							{{
								(!itemId ? $T('add') : $T('edit')) +
								' ' +
								(tab === 0 ? $T('penalty-type') : $T('reward-type'))
							}}
						</span>
						<v-spacer />
						<v-btn
							icon
							@click="dialog = false">
							<v-icon>$close</v-icon>
						</v-btn>
					</v-card-title>
					<v-card-text class="pb-3">
						<v-row dense class="ma-0">
							<!-- name -->
							<v-col cols="12">
								<v-text-field
									v-model="dialogData.name"
									:label="$T('inputs.name')"
									hide-details
									dense
									outlined
									:rules="[rules.required]"
								></v-text-field>
							</v-col>

							<!-- effect-type -->
							<v-col cols="12" class="py-2">
								<v-autocomplete
									v-model="dialogData.effectType"
									:items="tab === 0 ? penaltyTypes : rewordsTypes"
									item-value="id"
									item-text="name"
									:label="$T('inputs.effect-type')"
									hide-details
									dense
									outlined
									:rules="[rules.required]"
								></v-autocomplete>
							</v-col>

							<!-- value -->
							<v-col cols="12">
								<v-text-field
									v-model.number="dialogData.typeValue"
									:label="typeValueLabel"
									hide-details
									:prefix="dialogData.effectType === penaltyRewardValueType.rate ? '%' : null"
									dense
									outlined
									:rules="[rules.isNumber]"
								></v-text-field>
							</v-col>
							
							<template v-if="false">
								<!-- effect type -->
								<v-col cols="12">
									<v-autocomplete
										v-model="dialogData.effectType"
										:items="effectTypes"
										item-value="id"
										item-text="name"
										:label="$T('inputs.effect-type')"
										hide-details="auto"
										dense
										outlined
									></v-autocomplete>
								</v-col>
								
								<!-- financial type -->
								<v-col
									v-if="dialogData.effectType === 2"
									cols="12"
								>
									<v-radio-group
										v-model="dialogData.financialType"
										mandatory
										row
										dense
										hide-details="auto"
										class="ma-0"
									>
										<v-radio :label="$T('fixed')" :value="1" />
										<v-radio :label="$T('percentage-to-hour-price')" :value="2" />
									</v-radio-group>
								</v-col>
								
								<!-- financial value -->
								<v-col v-if="dialogData.effectType >= 2" cols="12" :class="{ 'mt-3': dialogData.effectType === 3 }">
									<v-text-field
										v-model.number="dialogData.effectValue"
										:label="
											dialogData.effectType === 1
												? dialogData.financialType === 1
													? $T('value')
													: $T('percent')
												: $T('days-count')
										"
										type="number"
										min="0"
										hide-spin-buttons
										:prefix="dialogData.financialType === 2 ? '%' : null"
										dense
										outlined
										hide-details="auto"></v-text-field>
								</v-col>
								
								<!-- maximum repetitions -->
								<v-col v-if="tab === 0" cols="12" class="mt-3">
									<v-text-field
										v-model.number="dialogData.maximumRepetitions"
										:label="$T('inputs.maximum-repetitions')"
										hide-details="auto"
										type="number"
										min="0"
										hide-spin-buttons
										dense
										outlined
										user-menu-picker
									></v-text-field>
								</v-col>
							</template>
						</v-row>
					</v-card-text>
					<v-card-actions>
						<v-spacer />
						<v-btn
							color="secondary"
							type="submit"
							:loading="submitLoading"
						>{{ $T('save') }}</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>
		<!-- delete dialog -->
		<v-delete-dialog
			v-model="deleteDialog"
			:module="'penaltiesRewards/deleteType'"
			:parameters="{ id: itemId ? itemId : null }"
			@on-delete="fetchAllPenaltiesRewardsTypes()">
			{{ 
				tab ===  0 ? $t('PenaltiesAndRewards.delete-message-penalty') : $T('PenaltiesAndRewards.delete-message-bonus') 
			}}
		</v-delete-dialog>

		<!-- tabs -->	
		<v-tabs v-model="tab">
			<v-tab>{{ $T('tabs.penalties') }}</v-tab>
			<v-tab>{{ $T('tabs.rewards') }}</v-tab>
		</v-tabs>
		<v-divider></v-divider>

		<div class="pa-2">
			<!-- add -->
			<v-row align="center" dense class="mb-1">
				<!-- type -->
				<v-col sm="4" cols="auto">
					<v-autocomplete
						v-model="type"
						:label="
							+tab === 0
								? $T('inputs.penalty-type')
								: $T('inputs.reward-type')
						"
						:items="+tab === 0 ? penalties : rewards"
						item-text="name"
						item-value="id"
						hide-details
						dense
						outlined
						clearable
					></v-autocomplete>
				</v-col>
				<v-spacer></v-spacer>
				<v-col cols="auto">
					<v-btn
						small
						color="primary"
						@click="dialog = true">
						<v-icon
							size="18"
							left>
							$add
						</v-icon>
						{{ $T('add') }}
					</v-btn>
				</v-col>
			</v-row>

			<!-- table -->
			<bee-handy-table
				:headers="headers"
				:items="tab === 0 ? type ? penalties.filter(c => c.id === type) : penalties  : type ? rewards.filter(c => c.id === type) : rewards"
				dense
				zebra
				pagination-on-scroll
				:items-per-page="20"
				:shown-rows-text="$T('shown-rows-text')"
				:height="$vuetify.breakpoint.height - 200"
				:data-table-props="{ disableSort: true }"
				hide-shown-rows-text="auto"
			>
				<!-- actions -->
				<template v-slot:item.actions="{ item }">
					<div class="d-flex justify-end">
						<v-btn
							icon
							color="edit-color"
							@click="itemId = item.id; dialog = true;"
						>
							<v-icon>$edit</v-icon>
						</v-btn>
	
						<v-btn
							icon
							color="alert-color"
							@click="itemId = item.id; deleteDialog = true;"
						>
							<v-icon>$delete</v-icon>
						</v-btn>
					</div>
				</template>
			</bee-handy-table>
		</div>
	</div>
</template>

<script>
import rules from '@/utils/validation rules';
import { penaltyRewardType, penaltyRewardValueType, penaltyRewardValueTypes } from '@/utils/enum';
import { mapGetters, mapState } from 'vuex';
export default {
	name: 'AddPenaltiesAndRewardsTypes',

	data() {
		return {
			deleteDialog: false,
			submitLoading: false,
			loading: false,

			dialog: false,

			tab: null,
			itemId: null,

			/* effectTypes: [
				{ id: 1, name: 'أثر على السجل' },
				{ id: 2, name: 'أثر مالي' },
				{ id: 3, name: 'أثر على الدوام' },
			],

			penaltyTypes0: [
				{ name: 'إنذار مسجل', effectType: 1, maximumRepetitions: 2 },
				{ name: 'تنبيه', effectType: 1, maximumRepetitions: 2 },
				{ name: 'خصم 3 أيام', effectType: 3, maximumRepetitions: 2 },
				{ name: 'خصم 5 أيام', effectType: 3, maximumRepetitions: 2 },
			],
			rewordsTypes0: [
				{ name: 'بطاقة شكر', effectType: 1 },
				{ name: 'مكافئة مادية', effectType: 2 },
			], */

			penaltyRewardValueType,
			penaltyTypes: penaltyRewardValueTypes,
			rewordsTypes: penaltyRewardValueTypes.slice(0, 2),
			rules,

			// data
			dialogData: {
				name: null,
				effectType: null,
				typeValue: null,

				maximumRepetitions: null,
				financialType: null,
				effectValue: null,
			},
			type: null
		};
	},

	watch: {
		dialog(val) {
			if (val) {
				if (this.itemId) {
					const item = this[this.tab === 0 ? 'getPenaltyById' : 'getRewardById'](this.itemId);
					this.dialogData.name = item.name;
					this.dialogData.effectType = item.valueType;
					this.dialogData.typeValue = item.value;
				}
			} else {
				this.$refs.form.reset();
				this.itemId = null;
				this.dialogData.name = null;
				this.dialogData.effectType = null;
				this.dialogData.maximumRepetitions = null;
				this.dialogData.financialType = null;
				this.dialogData.effectValue = null;
			}
		},
	},

	computed: {
		...mapState({
			penalties: state => state.penaltiesRewards.penalties,
			rewards: state => state.penaltiesRewards.rewards,
		}),
		...mapGetters({
			getPenaltyById: 'penaltiesRewards/getPenaltyById',
			getRewardById: 'penaltiesRewards/getRewardById',
		}),
		headers() {
			const headers = [
				{
					text: this.$T('headers.name'),
					name: 'name',
					value: 'name',
				},
				{
					text: this.$T('headers.effect-type'),
					name: 'effectType',
					value: 'valueType',
					noNotAvailableText: true,
					setValueExpr: (val) =>
						(this.tab === 0 ? this.penaltyTypes : this.rewordsTypes)
							.find((c) => c.id === val)?.name,
				},
				{
					text: this.$T('headers.value'),
					name: 'value',
					value: 'value',
					type: 'number-format',
					noNotAvailableText: true,
				},
				{
					text: '',
					name: 'actions',
					value: 'actions',
				},
			];

			/* if (this.tab === 0) {
				headers.splice(2, 1, {
					text: this.$T('headers.maximum-repetitions'),
					name: 'maximum-repetitions',
					value: 'maximumRepetitions',
				});
			} else {
				headers.splice(2, 1);
			} */
			return headers;
		},

		typeValueLabel() {
			switch(this.dialogData.effectType){
			case penaltyRewardValueType.rate: return this.$T('inputs.percent');
			case penaltyRewardValueType.days: return this.$T('inputs.days-count');
			default: return this.$T('inputs.value');
			}
		} 
	},

	methods: {
		submit() {
			if (this.$refs.form.validate()) {
				this.submitLoading = true;
				const actionName = !this.itemId ? 'createType' : 'updateType';
				this.$store.dispatch(`penaltiesRewards/${actionName}`, {
					id: this.itemId,
					type: this.tab === 0 ? penaltyRewardType.penalty : penaltyRewardType.rewards,
					valueType: this.dialogData.effectType,
					value: this.dialogData.typeValue,
					name: this.dialogData.name 
				}).then(() => {
					this.fetchAllPenaltiesRewardsTypes()
					this.$eventBus.$emit(
						'show-snackbar',
						!this.itemId ? this.$T('messages.add-done') : this.$T('messages.edit-done'),
						{ done: true }
					);
					this.dialog = false;
				}).catch((err) => {
					err.default && err.default(
						!this.itemId ? this.$T('messages.add-fail') : this.$T('messages.edit-fail'),
						false
					);
				}).finally(() => {
					this.submitLoading = false;
				})
			}
		},

		fetchAllPenaltiesRewardsTypes() {
			this.loading = true;
			this.$store.dispatch('penaltiesRewards/fetchAllTypes').finally(() => {
				this.loading = true;
			})
		}
	},

	created() {
		this.fetchAllPenaltiesRewardsTypes();
	}
};
</script>

<style></style>
